import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { pageURLs } from "@/constants";
import { fetchUserStatistics } from "../../../../actions/userprofile";
import { USER_TYPE_AGENT, BUY, LONG_RENT } from "../../../../constants/common";
import { getAddressDisplay } from "../../../../helpers/common";
import { formatCompactPrice } from "../../../../helpers/priceCapUtils";
import noImage from "../../../../images/no-image-placeholder.png";
import PremiumPlanModal from "../../../PlanUpgrade/PremiumPlanModal";
import BasicPlanModal from "../../../PlanUpgrade/BasicPlanModal";
import "./property_dropdown_list.scss";
import { useMLSAgentAtGitchaRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";

const PropertyDropdownList = ({
  propertyList,
  selectedProperty,
  setSelectedProperty,
  adType,
  userType,
  selectedClient,
  userDetails,
  updateReloadParam,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const [showBasicPlanModal, setBasicPlanModal] = useState(false);

  const userStatictics = useSelector(
    (state) => state?.userProfile?.userStatictics,
  );
  const userPrivileges = useSelector(
    (state) => state?.userProfile?.userPrivileges,
  );
  const [userCount, setUserCount] = useState({});
  const [allowedCount, setAllowedCount] = useState({});

  useEffect(() => {
    if (userDetails?.User_Plan_Id) {
      dispatch(fetchUserStatistics({ UserPlanId: userDetails.User_Plan_Id }));
    }
  }, [userDetails]);

  useEffect(() => {
    if (userPrivileges?.length > 0) setAllowedCount(userPrivileges[0]);
  }, [userPrivileges]);

  useEffect(() => {
    if (userStatictics?.length > 0) setUserCount(userStatictics[0]);
  }, [userStatictics]);

  const { redirectToBLSEnv } = useMLSAgentAtGitchaRedirect()

  const getPrice = (property) => {
    if (adType === BUY) return property.AskingPrice_Sell;
    if (adType === LONG_RENT) return property.AskingPrice_Rent;
    return property.AskingPrice_Night;
  };

  const filtered = propertyList.filter((p) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const loc = (p.Location || "").toLowerCase();
    const city = (p.City || "").toLowerCase();
    const state = (p.State || "").toLowerCase();
    return loc.includes(term) || city.includes(term) || state.includes(term);
  });

  const handleAddProperty = () => {
    redirectToBLSEnv({
      path: `${pageURLs.agentCreateInventory}`,
      onGitchaEnvFallback: () => {
        if (userCount?.Inventory >= allowedCount?.Inventory) {
          if (userType === USER_TYPE_AGENT) {
            setPremiumPlanModal(true);
          } else {
            setBasicPlanModal(true);
          }
        } else {
          if (userType === USER_TYPE_AGENT) {
            navigate(pageURLs.agentCreateInventory, {
              state: {
                data: {
                  wantAdUuid: params.wantAdId,
                  clientId: selectedClient?.value,
                  clientName: selectedClient?.label,
                  isAgentAsSeller: selectedClient?.AgentAsSeller,
                },
              },
            });
          } else {
            navigate(pageURLs.memberCreateInventory);
          }
        }
      }
    })
  };

  return (
    <div className="property-dropdown-overlay">
      {propertyList.length > 3 && (
        <div className="property-dropdown-overlay__search">
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>
      )}

      <div className="property-dropdown-overlay__list">
        {filtered.map((property) => {
          const isSelected = selectedProperty?.Inv_Id === property.Inv_Id;
          const addr = getAddressDisplay(
            property.Location,
            property.City,
            property.State,
            property.ZIP,
          );
          const price = getPrice(property);

          return (
            <button
              key={property.Inv_Id}
              className={`property-dropdown-item ${isSelected ? "property-dropdown-item--selected" : ""}`}
              onClick={() => setSelectedProperty(property)}
              type="button"
            >
              <div className="property-dropdown-item__thumb">
                <img src={property.photo || noImage} alt="" />
              </div>
              <div className="property-dropdown-item__info">
                <span className="property-dropdown-item__addr">
                  {addr.before || property.Location}
                </span>
                {addr.after && (
                  <span className="property-dropdown-item__city">
                    {addr.after}
                  </span>
                )}
              </div>
              {price > 0 && (
                <span className="property-dropdown-item__price">
                  {formatCompactPrice(price)}
                </span>
              )}
              {isSelected && (
                <span className="property-dropdown-item__check">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M2.5 7L5.5 10L11.5 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="property-dropdown-overlay__empty">No properties match your search</p>
        )}
      </div>

      <div className="property-dropdown-overlay__footer">
        <button
          className="btn btn-secondary w-100"
          onClick={handleAddProperty}
          type="button"
        >
          Add Property
        </button>
      </div>

      <PremiumPlanModal
        userDetails={userDetails}
        updateReloadParam={updateReloadParam}
        show={showPremiumPlanModal}
        onHide={() => setPremiumPlanModal(false)}
        fromInventoryListing={true}
        showPlanModal={() => setPremiumPlanModal(true)}
      />
      <BasicPlanModal
        userDetails={userDetails}
        show={showBasicPlanModal}
        updateReloadParam={updateReloadParam}
        onHide={() => setBasicPlanModal(false)}
        fromInventoryListing={true}
        showPlanModal={() => setBasicPlanModal(true)}
      />
    </div>
  );
};

export default PropertyDropdownList;
